import React, { useEffect, useState } from "react";
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Chip, CircularProgress, Alert, Button, Stack, Tabs, Tab,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import DownloadIcon from "@mui/icons-material/Download";
import ThemeToggle from "./ThemeToggle";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";
const BRAND = { navy: "#0a0e27", accent: "#637cdb", accentDark: "#3F6AC0" };

const STATUS_COLOR = { paid: "warning", shipped: "info", received: "success" };
const STATUS_LABEL = { paid: "Ready to Ship", shipped: "Shipped — Awaiting Return", received: "Received" };

function glassCardSx(isDark) {
  return {
    borderRadius: "16px", overflow: "hidden",
    border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(10,14,39,0.1)"}`,
    bgcolor: isDark ? "rgba(17,24,39,0.62)" : "rgba(255,255,255,0.68)",
    backdropFilter: "blur(20px) saturate(140%)",
    WebkitBackdropFilter: "blur(20px) saturate(140%)",
    boxShadow: isDark ? "0 24px 80px rgba(0,0,0,0.4)" : "0 24px 80px rgba(10,14,39,0.08)",
  };
}

function tableHeadSx(isDark) {
  return {
    "& th": {
      fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", fontSize: 11,
      color: isDark ? "#94a3b8" : "#64748b",
      borderBottom: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(10,14,39,0.08)"}`,
    },
  };
}

function tableRowSx(isDark) {
  return { "& td": { borderBottom: `1px solid ${isDark ? "rgba(255,255,255,0.05)" : "rgba(10,14,39,0.05)"}` } };
}

export default function LogisticsDashboard() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const navigate = useNavigate();
  const staff = JSON.parse(localStorage.getItem("logisticsStaff") || "null");

  const [tab, setTab] = useState(0);
  const [queue, setQueue] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const bg = isDark ? "#0a0f1e" : "#f0f4ff";

  const load = async () => {
    try {
      const headers = { Authorization: `Bearer ${staff?.token}` };
      const [q, h] = await Promise.all([
        axios.get(`${API_BASE}/logistics/queue`, { headers }),
        axios.get(`${API_BASE}/logistics/history`, { headers }),
      ]);
      setQueue(q.data.queue || []);
      setHistory(h.data.history || []);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        localStorage.removeItem("logisticsStaff");
        navigate("/logistics-login", { replace: true });
        return;
      }
      setError(err.response?.data?.detail || "Failed to load the shipping queue.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!staff?.token) { navigate("/logistics-login", { replace: true }); return; }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDownload = async (order) => {
    setBusyId(order._id);
    try {
      const headers = { Authorization: `Bearer ${staff?.token}` };
      const res = await axios.get(`${API_BASE}/logistics/queue/${order._id}/e-rec`, { headers, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `E-REC_${order.customer_id}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch {
      setError("Failed to generate the E-REC. Please try again.");
    } finally {
      setBusyId(null);
    }
  };

  const handleMarkShipped = async (order) => {
    setBusyId(order._id);
    try {
      const headers = { Authorization: `Bearer ${staff?.token}` };
      await axios.post(`${API_BASE}/logistics/queue/${order._id}/mark-shipped`, {}, { headers });
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to mark this order shipped.");
    } finally {
      setBusyId(null);
    }
  };

  const handleMarkReceived = async (order) => {
    setBusyId(order._id);
    try {
      const headers = { Authorization: `Bearer ${staff?.token}` };
      await axios.post(`${API_BASE}/logistics/queue/${order._id}/mark-received`, {}, { headers });
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to mark this order received.");
    } finally {
      setBusyId(null);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("logisticsStaff");
    navigate("/logistics-login", { replace: true });
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: "100vh", bgcolor: bg, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <CircularProgress sx={{ color: BRAND.accent }} />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: bg, position: "relative", overflow: "hidden" }}>
      {/* Animated background blobs — same treatment as the main app's login/glass pages */}
      <Box sx={{ position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none", zIndex: 0 }}>
        {[
          { top: "-12%", left: "-6%", size: 480, color: isDark ? "rgba(10,14,39,0.18)" : "rgba(10,14,39,0.06)", delay: 0 },
          { top: "50%", right: "-10%", size: 420, color: isDark ? "rgba(99,124,219,0.07)" : "rgba(99,124,219,0.1)", delay: 1.2 },
        ].map((b, i) => (
          <motion.div key={i} style={{
            position: "absolute", top: b.top, left: b.left, right: b.right,
            width: b.size, height: b.size, borderRadius: "50%",
            background: b.color, filter: "blur(90px)",
          }}
            animate={{ scale: [1, 1.1, 1], opacity: [0.8, 1, 0.8] }}
            transition={{ duration: 9 + i * 2, repeat: Infinity, delay: b.delay, ease: "easeInOut" }}
          />
        ))}
      </Box>

      <Box sx={{ position: "relative", zIndex: 10, px: { xs: 2, md: 5 }, py: 4 }}>
        <motion.div initial={{ y: -16, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.45, ease: "easeOut" }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, flexWrap: "wrap", gap: 2 }}>
            <Box>
              <Box sx={{ display: "inline-flex", alignItems: "center", bgcolor: BRAND.navy, borderRadius: "8px", px: 1.25, py: 0.75, mb: 1.5 }}>
                <img src="/logo-light.png" alt="Proove Genomics" style={{ height: 20, width: "auto", display: "block" }} />
              </Box>
              <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.12em", color: BRAND.accent, mb: 0.5 }}>
                LOGISTICS
              </Typography>
              <Typography sx={{ fontSize: 26, fontWeight: 800, color: isDark ? "#f1f5f9" : BRAND.navy }}>
                Kit Shipping Queue
              </Typography>
              <Typography sx={{ fontSize: 13, color: isDark ? "#94a3b8" : "#64748b", mt: 0.5 }}>
                Signed in as {staff?.name || staff?.email}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <ThemeToggle />
              <Button variant="outlined" onClick={handleLogout} sx={{ textTransform: "none", borderRadius: "10px" }}>Log out</Button>
            </Stack>
          </Box>
        </motion.div>

        <AnimatePresence>
          {error && (
            <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}>
              <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setError(null)}>{error}</Alert>
            </motion.div>
          )}
        </AnimatePresence>

        <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 3 }}>
          <Tab label={`Active Queue (${queue.length})`} sx={{ textTransform: "none", fontWeight: 700 }} />
          <Tab label={`My Shipping History (${history.length})`} sx={{ textTransform: "none", fontWeight: 700 }} />
        </Tabs>

        {tab === 0 && (
          <motion.div initial={{ y: 16, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.5, ease: "easeOut" }}>
            <TableContainer sx={{ ...glassCardSx(isDark), overflowX: "auto" }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={tableHeadSx(isDark)}>
                    <TableCell>Customer ID</TableCell>
                    <TableCell>Child</TableCell>
                    <TableCell>Shipping Address</TableCell>
                    <TableCell>Billing Address</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {queue.map((o) => (
                    <TableRow key={o._id} hover sx={tableRowSx(isDark)}>
                      <TableCell sx={{ fontFamily: "monospace", fontSize: 12.5 }}>{o.customer_id}</TableCell>
                      <TableCell>{o.child_first_name} {o.child_last_name}</TableCell>
                      <TableCell sx={{ fontSize: 12 }}>
                        {o.shipping_address?.line1}, {o.shipping_address?.city}, {o.shipping_address?.state} {o.shipping_address?.zip}
                      </TableCell>
                      <TableCell sx={{ fontSize: 12 }}>
                        {o.billing_address?.line1}, {o.billing_address?.city}, {o.billing_address?.state} {o.billing_address?.zip}
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={STATUS_LABEL[o.order_status] || o.order_status} color={STATUS_COLOR[o.order_status] || "default"} />
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <Button size="small" variant="outlined" startIcon={<DownloadIcon fontSize="small" />}
                            disabled={busyId === o._id} onClick={() => handleDownload(o)} sx={{ textTransform: "none", borderRadius: "8px" }}>
                            E-REC
                          </Button>
                          {o.order_status === "paid" && (
                            <Button size="small" variant="contained" disabled={busyId === o._id}
                              onClick={() => handleMarkShipped(o)} sx={{ textTransform: "none", borderRadius: "8px", background: `linear-gradient(135deg, ${BRAND.navy} 0%, ${BRAND.accentDark} 100%)` }}>
                              Mark Shipped
                            </Button>
                          )}
                          {o.order_status === "shipped" && (
                            <Button size="small" variant="contained" color="success" disabled={busyId === o._id}
                              onClick={() => handleMarkReceived(o)} sx={{ textTransform: "none", borderRadius: "8px" }}>
                              Mark Received
                            </Button>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                  {queue.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4, color: "#94a3b8" }}>
                        No kits in the queue right now.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </motion.div>
        )}

        {tab === 1 && (
          <motion.div initial={{ y: 16, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.5, ease: "easeOut" }}>
            <TableContainer sx={{ ...glassCardSx(isDark), overflowX: "auto" }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={tableHeadSx(isDark)}>
                    <TableCell>Customer ID</TableCell>
                    <TableCell>Child</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Shipped</TableCell>
                    <TableCell>Received</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {history.map((o) => (
                    <TableRow key={o._id} hover sx={tableRowSx(isDark)}>
                      <TableCell sx={{ fontFamily: "monospace", fontSize: 12.5 }}>{o.customer_id}</TableCell>
                      <TableCell>{o.child_first_name} {o.child_last_name}</TableCell>
                      <TableCell>
                        <Chip size="small" label={STATUS_LABEL[o.order_status] || o.order_status} color={STATUS_COLOR[o.order_status] || "default"} />
                      </TableCell>
                      <TableCell sx={{ fontSize: 12.5 }}>{o.shipped_at ? new Date(o.shipped_at).toLocaleString() : "—"}</TableCell>
                      <TableCell sx={{ fontSize: 12.5 }}>{o.received_at ? new Date(o.received_at).toLocaleString() : "—"}</TableCell>
                    </TableRow>
                  ))}
                  {history.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 4, color: "#94a3b8" }}>
                        You haven't shipped or received any kits yet.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </motion.div>
        )}
      </Box>
    </Box>
  );
}
